<template>
  <div>
    <!--loader-->
    <section v-if="loading" class="section section--loader">
      <loader size="large" :show_timing_slot="true" />
    </section>

    <!--error state (custom home only)-->
    <section v-else-if="info_error" class="section section--error">
      <b-container>
        <b-row>
          <b-col class="text-center py-5">
            <h2 class="mb-3">Contenuto non disponibile</h2>
            <p class="text-muted">
              Non è stato possibile caricare i contenuti richiesti.
              <br />
              Riprova più tardi o contatta l'assistenza.
            </p>
          </b-col>
        </b-row>
      </b-container>
    </section>

    <div v-else>
      <!-- Hero Slider Section -->
      <section class="section section--slider">
        <swiper-container
          ref="swiper-container"
          init="false"
          class="section--slider-main"
          pagination="true"
        >
          <swiper-slide
            v-for="(item, item_index) in slider_items"
            :key="`hero:slide:${_uid}:${item_index}`"
            class="section--slider-item"
            :class="{ overlay: item.overlay }"
          >
            <div
              class="section--slider-image"
              :style="`background-image:url(${item.wallpaper})`"
            ></div>
            <b-container class="captions">
              <div class="text">
                <div class="vb-heading h2" v-html="item.title"></div>
                <div class="pr-3" v-html="item.text"></div>
              </div>
            </b-container>
          </swiper-slide>
        </swiper-container>
      </section>

      <!-- Booking Engine Section -->
      <section
        v-if="is_authenticated()"
        class="section section--booking-engine"
      >
        <b-container>
          <booking-engine
            ref="booking-engine"
            :date_constraints="{
              min_date: info.search_period_start,
              max_date: info.search_period_end,
            }"
            :customizations="{
              hide_location_search: !info.allow_location_search,
            }"
            :super_in="{
              name: info.name,
              title: info.title,
              children: info.children,
            }"
            @search="
              () => {
                $store.commit('liveeps/selected_invadobay', info);
                this.$router.push({ name: 'results' });
              }
            "
            @select-child="select_child"
          />
        </b-container>
      </section>

      <div class="sections">
        <!-- properties -->
        <section v-if="preview_search" class="section">
          <b-container>
            <b-row>
              <b-col>
                <p class="text-muted">
                  <span v-if="preview_search_loading">
                    Verificando disponibilità
                  </span>
                  <span v-else>Disponibilità verificate in tempo reale</span>
                  {{ availability_dates }}
                </p>
              </b-col>
            </b-row>
            <b-row v-if="preview_search_loading">
              <b-col sm="12" class="section section--loader">
                <loader size="large" :show_timing_slot="true" />
              </b-col>
            </b-row>
            <b-row v-else-if="properties.length === 0">
              <b-col sm="12" class="section section--loader">
                <h3 style="width: 100%; text-align: center">
                  Nessuna struttura da mostrare {{ availability_dates }}
                </h3>
              </b-col>
            </b-row>
            <b-row v-else>
              <b-col
                v-for="property in properties.slice(0, 8)"
                :key="property.property_id"
                xl="3"
                lg="3"
                md="6"
                sm="12"
                xs="12"
                class="pb-4"
              >
                <invadobay-property-card
                  :property="property"
                  :checkin="moment(suggested_dates.checkin || checkin)"
                  :checkout="moment(suggested_dates.checkout || checkout)"
                />
              </b-col>
            </b-row>

            <b-row>
              <b-col xl="12">
                <div class="w-100 text-right bold">
                  <button
                    v-if="
                      !info.allow_location_search && !preview_search_loading
                    "
                    class="btn btn-ghost bold text-info"
                    @click="
                      () => {
                        if ($refs['booking-engine']) {
                          try {
                            $refs['booking-engine'].search();
                          } catch (e) {
                            log('Vedi tutti i risultati', e);
                          }
                        }
                      }
                    "
                  >
                    Vedi tutti i risultati
                  </button>
                </div>
              </b-col>
            </b-row>
          </b-container>
        </section>
        <section v-else class="p-3">
          <!-- empty -->
        </section>
      </div>

      <!-- related invadobays -->
      <invadobays
        v-if="show_related_invadobays"
        ref="related_invadobays"
        :options="{
          config: [
            {
              title: '',
              description: '',
              preview_type: 'compact',
              related_to: info.id,
            },
          ],
        }"
      />
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import { get_config, is_authenticated } from '@src/utilities';
  import {
    format_booking_dates_range,
    get_suggested_booking_dates,
  } from '@liveeps/utilities';
  import {
    get_invadobays,
    search_invadobay_preview_properties,
  } from '../blocks/invadobay/utilities';
  import moment from 'moment';
  import { md } from '@running_app_src/scss/abstract/_custom.scss';
  import InvadobayPropertyCard from '@liveeps/components/blocks/invadobay/invadobay-property-card.vue';
  import Invadobays from '@liveeps/components/blocks/invadobay/invadobays.vue';

  export default {
    name: 'invadobay-view',
    components: {
      'invadobay-property-card': InvadobayPropertyCard,
      invadobays: Invadobays,
    },
    props: {
      slug: {
        type: String,
        default: '',
      },
      is_custom_home: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        info: {},
        info_loading: true,
        info_error: false,

        properties: [],
        preview_search_loading: false,

        suggested_dates: {
          checkin: moment(),
          checkout: moment(),
        },
        // Slider data
        slider_items: [],
        slider_index: 0,
        swiper_instance: {},
        swiper_params: {
          slidesPerView: 1,
          loop: true,
          autoHeight: true,
          allowTouchMove: false,
          breakpoints: {
            [md.replace(/[^\d.]+/g, '')]: {
              slidesPerView: 2,
            },
          },
          on: {
            init: (s) => {
              this.slider_index = s.realIndex;
              this.slider_items = this.get_slider_items(s.loopedSlides);
              s.update();
            },
            slidePrevTransitionStart: () => {
              this.slider_index =
                (this.slider_index === 0
                  ? this.slider_items.length / 2
                  : this.slider_index) - 1;
            },
            slideNextTransitionStart: () => {
              this.slider_index =
                this.slider_index === this.slider_items.length / 2 - 1
                  ? 0
                  : this.slider_index + 1;
            },
          },
        },
      };
    },

    computed: {
      ...mapState('liveeps', ['searching']),
      ...mapState({
        occupancy: (state) => state.occupancy,
        // liveeps
        checkin: (state) => state.liveeps.checkin,
        checkout: (state) => state.liveeps.checkout,
      }),
      loading() {
        return this.info_loading || this.invadobay_sections_loading;
      },
      preview_search() {
        return this.get_config(
          'invadobays.customizations.preview_search',
          true
        );
      },
      show_related_invadobays() {
        return (
          this.get_config(
            'invadobays.customizations.show_related_invadobays'
          ) || true
        );
      },
      invadobay_sections: {
        get() {
          try {
            return JSON.parse(this.invadobay_sections_data);
          } catch {
            return [];
          }
        },
        set(value) {
          this.invadobay_sections_data = JSON.stringify(value);
        },
      },
      invadobay_sections_loading() {
        return (this.$refs['related_invadobays'] || {})
          .invadobay_sections_loading;
      },
      availability_dates() {
        return this.format_booking_dates_range(
          {
            date_start: this.suggested_dates.checkin,
            date_end: this.suggested_dates.checkout,
          },
          true
        );
      },
    },
    watch: {
      slug: {
        handler() {
          this.$nextTick(() => {
            this.fetch_invadobay_info();
          });
        },
      },
    },
    created() {
      // La pagina è accessibile ai soli utenti autenticati
      if (!this.is_authenticated()) {
        this.$router.push({ name: 'homepage' });
        return;
      }
      this.fetch_invadobay_info();
    },
    methods: {
      moment,
      get_config,
      is_authenticated,
      get_invadobays,
      format_booking_dates_range,
      get_suggested_booking_dates,
      // Navigate to a child InVadobay (SuperIn child CTA). Routing only: the
      // slug param change triggers the slug watcher → fetch_invadobay_info(),
      // so all existing view logic re-runs without a remount.
      select_child(child) {
        if (!child || !child.slug || this.$route.params.slug === child.slug) {
          return;
        }
        this.$router.push({ name: 'invadobay', params: { slug: child.slug } });
      },
      get_slider_items(slides = 1) {
        // Create slider items from InvadoBay info
        if (!this.info || !this.info.cover_media) {
          return [];
        }

        const item = {
          wallpaper: this.info.cover_media.url,
          title: this.info.title,
          text: this.info.description,
          overlay: true,
        };

        // Duplicate the item to create 2 slides
        const items = [item, item];

        // If slides == 1, return simple array (mobile)
        if (slides === 1) return items;

        // If slides == 2, manipulate for desktop view (same as homepage)
        const last = items.pop();
        return [last, ...items, last, ...items];
      },
      get_ref_height(ref) {
        if (!ref) {
          return 0;
        }
        return ref.clientHeight || 0;
      },
      fetch_invadobay_info() {
        this.info_loading = true;
        this.info_error = false;
        this.get_invadobays([this.slug])
          .then((res) => {
            if (res && res.length) {
              // Set invadobay info
              this.info = res[0];

              // Set booking engine params
              const { checkin, checkout } = this.get_suggested_booking_dates({
                date_start: this.info.search_period_start,
                date_end: this.info.search_period_end,
              });
              this.suggested_dates = { checkin, checkout };
              this.$store.commit('liveeps/checkin', checkin);
              this.$store.commit('liveeps/checkout', checkout);
              // If location search is active user must put in a place to go to
              this.$store.commit('liveeps/location', {
                label: this.info.allow_location_search ? '' : this.info.title,
              });

              // Search previews
              if (this.preview_search) {
                this.fetch_properties_preview();
              }
            } else {
              // Custom home: show error instead of redirect
              if (this.is_custom_home) {
                this.info_error = true;
              } else {
                this.$router.push({ name: 'homepage' });
              }
            }
          })
          .catch(() => {
            // Custom home: show error instead of redirect
            if (this.is_custom_home) {
              this.info_error = true;
            } else {
              this.$router.push({ name: 'homepage' });
            }
          })
          .finally(() => {
            this.info_loading = false;
            window.scrollTo(0, 0);

            // Initialize slider items
            this.slider_items = this.get_slider_items();

            this.$nextTick(() => {
              // Initialize swiper
              if (this.$refs['swiper-container']) {
                const swiper_instance = this.$refs['swiper-container'];
                Object.assign(swiper_instance, this.swiper_params);
                swiper_instance.initialize();
                this.swiper_instance = swiper_instance;
              }
            });
          });
      },
      fetch_properties_preview() {
        const search_promise = search_invadobay_preview_properties(
          this.info,
          this.occupancy
        );
        if (search_promise) {
          this.preview_search_loading = true;
          search_promise
            .then((success) => {
              this.properties = success.data.data.properties;
            })
            .finally(() => {
              this.preview_search_loading = false;
            });
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .section {
    &--slider {
      position: relative;
      margin-top: 0;
      background: var(--primary);
      @include media-breakpoint-up(md) {
        margin-bottom: 0;
      }

      &-item {
        position: relative;
      }

      &-image {
        background-size: cover;
        background-position: center center;
        height: 280px;
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
        }

        @include media-breakpoint-up(md) {
          height: 320px;
        }
        @include media-breakpoint-up(lg) {
          height: 360px;
        }
      }

      &-nav {
        position: absolute;
        z-index: 1;
        top: 130px;
        transform: translateY(-50%);
        padding: $grid-gutter-width/2;
        cursor: pointer;

        &--prev {
          left: 0;

          ::v-deep {
            svg {
              filter: drop-shadow(-2px 0 0 $white);
            }
          }
        }

        &--next {
          right: 0;

          ::v-deep {
            svg {
              filter: drop-shadow(2px 0 0 $white);
            }
          }
        }

        @include media-breakpoint-up(md) {
          top: 50%;
        }
      }

      .captions {
        margin-top: $grid-gutter-width/2;
        padding-bottom: $grid-gutter-width/2;
        background: var(--primary);
        display: flex;

        .icon {
          padding-top: 3px;
        }

        .text {
          padding-left: $grid-gutter-width/2;
          color: white;

          * {
            line-height: 120%;
          }

          .vb-heading {
            margin-bottom: 4px;
            color: white;
            line-height: 110%;
          }
        }

        @include media-breakpoint-between(md, lg) {
          padding-left: 16% !important;

          .vb-heading {
            font-size: 30px !important;
            max-width: 90% !important;
          }
        }
      }

      .custom-pagination {
        display: none;
        position: absolute;
        z-index: 1;
        bottom: 100px;
        left: calc(
          2.5% + 36px + #{$grid-gutter-width/2}
        ); // margine sinistro + larghezza icona + padding contenitore captions

        &-item {
          margin-right: 10px;
          height: 2px;
          width: 30px;
          background: var(--primary);
          &.active {
            background: var(--primary);
          }
        }
      }

      @include media-breakpoint-up(md) {
        &-item {
          &:after {
            content: '';
            position: absolute;
            background: var(--primary);
            top: 0;
            left: 0;
            right: 0;
            width: 100%;
            height: 100%;
            transition: $transition-base;
            opacity: 0;
          }
        }

        .custom-pagination {
          display: flex;
          margin-left: 10.5%;
        }

        .swiper-slide-active {
          .captions {
            display: none;
          }

          &:after {
            opacity: 1;
          }
        }

        .swiper-slide-next {
          position: relative;

          .captions {
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
            transform: translateY(-50%) translateX(-100%);
            margin-top: 0;
            padding: 0 $grid-gutter-width 0 26%;

            .text {
              color: white;

              .vb-heading {
                font-size: 36px;
                line-height: 120%;
                color: white;
              }
            }

            .icon {
              padding-top: 8px;
            }
          }
        }
      }
    }

    &--booking-engine {
      position: relative;
      z-index: 9999;

      @include media-breakpoint-up(md) {
        margin-top: -33px;
      }
    }
  }

  .sections > .section {
    @include media-breakpoint-up(lg) {
      &:last-child {
        margin-bottom: 100px;
      }
    }
  }
</style>
